import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DateInput } from "@/components/DateInput";
import { MODULE_THEMES, themeVars } from "@/lib/moduleTheme";

// voucher type -> module accent
const FORM_MODULE = {
  sale: "sales", sales: "sales", sales_order: "sales",
  purchase: "purchase", purchase_order: "purchase",
  adjustment: "inventory", transfer: "inventory",
};
import { MasterSelect } from "@/components/MasterSelect";
import { ShadeDialog } from "@/components/ShadeDialog";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Layers, Plus, Trash2 } from "lucide-react";
import { QuickItemDialog } from "@/components/QuickItemDialog";

const emptyLine = { fabric_id: "", label: "", shade: "", bale_no: "", lr_no: "", than: "", meter: "",
                    rate: "", discount_pct: "", gst_pct: "", length_text: "", product_type: "shade_wise" };

const MIN_ROWS = 11;
// which line field each draggable column writes to
const FILL_FIELD = { item: "label", shade: "shade", than: "than", qty: "meter", rate: "rate",
                     disc: "discount_pct", gst: "gst_pct", bale: "bale_no", lr: "lr_no" };
const num = (v) => parseFloat(v) || 0;
const rs = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "10.8*1 12.5*1" -> { meter: 23.3, than: 2 }; a bare "12" counts as one piece of 12 mtr
export const parseLengths = (text) => {
  let meter = 0;
  let than = 0;
  (text || "")
    .replace(/×/g, "*")
    .split(/[\s,;]+/)
    .filter(Boolean)
    .forEach((token) => {
      const [len, pcs] = token.split("*");
      const l = parseFloat(len);
      if (Number.isNaN(l)) return;
      const p = pcs === undefined || pcs === "" ? 1 : parseFloat(pcs) || 0;
      meter += l * p;
      than += p;
    });
  return { meter: Math.round(meter * 100) / 100, than: Math.round(than * 100) / 100 };
};

const lineQty = (l) => {
  if (l.product_type === "without_shade" && (l.length_text || "").trim()) return parseLengths(l.length_text);
  return { meter: num(l.meter), than: num(l.than) };
};

const lineTotal = (l) => {
  const gross = num(l.rate) * lineQty(l).meter;
  return Math.round((gross - (gross * num(l.discount_pct)) / 100) * 100) / 100;
};

const padRows = (ls) => {
  const out = [...ls];
  while (out.length < MIN_ROWS) out.push({ ...emptyLine });
  return out;
};

const COL_LABEL = { sn: "S.N.", item: "Item", shade: "Shade", qty: "Qty.", than: "Than", unit: "Unit",
                    rate: "Price (Rs.)", disc: "Dis %", gst: "GST %", amount: "Amount (Rs.)",
                    bale: "Bale", lr: "LR", del: "" };
const COL_WIDTH = { sn: "w-[38px]", shade: "w-[90px]", qty: "w-[80px]", than: "w-[70px]", unit: "w-[60px]",
                    rate: "w-[80px]", disc: "w-[58px]", gst: "w-[58px]", amount: "w-[110px]",
                    bale: "w-[80px]", lr: "w-[80px]", del: "w-[30px]" };

// dense voucher-screen widgets
const cellCls =
  "h-[26px] w-full border-0 bg-transparent px-1.5 text-[13px] leading-none text-zinc-900 outline-none focus:bg-[#FFF8B0] focus:ring-1 focus:ring-inset focus:ring-[#8B7500]";
const headCell = "border border-[var(--mod-border)] bg-[var(--mod-head)] px-1.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--mod-dark)]";
const bodyCell = "border border-[var(--mod-border)] bg-transparent p-0";
const fieldCls =
  "h-[24px] border border-[#B9B98A] bg-white px-1.5 text-[13px] font-semibold text-[#1F3D7A] outline-none focus:bg-[#FFF8B0]";
const Lbl = ({ children }) => (
  <span className="whitespace-nowrap text-[12px] font-semibold text-[var(--mod-dark)]">{children}</span>
);
const Box = ({ title, children, className = "" }) => (
  <fieldset className={`border border-[var(--mod-border)] bg-[var(--mod-tint)] px-2 pb-2 ${className}`}>
    <legend className="px-1 text-[12px] font-bold text-[var(--mod-primary)]">{title}</legend>
    {children}
  </fieldset>
);

export const TransactionForm = ({ open, onOpenChange, config, editRow, onSaved }) => {
  const formTheme = MODULE_THEMES[FORM_MODULE[config.type] || "sales"];
  const purchaseSide = config.type === "purchase" || config.type === "purchase_order";
  // purchase vouchers default to the item's purchase price, sales to the wholesale rate
  const defaultRate = (f) => (purchaseSide ? f.purchase_price : f.wsr);
  const [head, setHead] = useState({ party: "", centre: "", to_centre: "", ref_no: "", bill_no: "", date: "", notes: "", discount_pct: "", discount_amount: "", other_discount_pct: "", other_discount_amount: "", interstate: false, round_off: false, gst_pct: "" });
  const [lines, setLines] = useState(padRows([]));
  const [expenses, setExpenses] = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [parties, setParties] = useState([]);
  const [centres, setCentres] = useState([]);
  const [saving, setSaving] = useState(false);
  const gridRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editRow) {
      setHead({
        party: editRow.party || "",
        centre: editRow.centre || "",
        to_centre: editRow.to_centre || "",
        ref_no: editRow.ref_no || "",
        bill_no: editRow.bill_no || "",
        date: editRow.date || new Date().toISOString().slice(0, 10),
        notes: editRow.notes || "",
        discount_pct: editRow.discount_pct || "",
        discount_amount: editRow.bill_discount && !editRow.discount_pct ? editRow.bill_discount : "",
        other_discount_pct: editRow.other_discount_pct || "",
        other_discount_amount: editRow.other_discount && !editRow.other_discount_pct ? editRow.other_discount : "",
        interstate: !!editRow.interstate,
        round_off: !!editRow.round_off_enabled,
        gst_pct: editRow.gst_pct ?? "",
      });
      setExpenses(
        (editRow.expenses || []).map((e) => ({ head: e.head || "", amount: e.pct ? "" : e.amount ?? "",
                                               pct: e.pct || "", taxable: !!e.taxable }))
      );
      setLines(
        padRows(
          editRow.items.map((it) => ({
            fabric_id: it.fabric_id,
            label: it.quality_name,
            shade: it.shade_number || "",
            bale_no: it.bale_no || "",
            lr_no: it.lr_no || "",
            product_type: it.product_type || "shade_wise",
            length_text: it.length_text || "",
            than: it.than ?? "",
            meter: it.meter ?? "",
            rate: it.rate ?? "",
            discount_pct: it.discount_pct || "",
            gst_pct: it.gst_pct ?? "",
          }))
        )
      );
    } else {
      setHead((h) => ({ party: "", centre: h.centre || "", to_centre: "", ref_no: "", bill_no: "", date: new Date().toISOString().slice(0, 10), notes: "",
                discount_pct: "", discount_amount: "", other_discount_pct: "", other_discount_amount: "", interstate: false, round_off: false, gst_pct: "" }));
      setLines(padRows([]));
      setExpenses([]);
    }
    api.get("/fabrics").then(({ data }) => setFabrics(data)).catch(() => {});
    api.get("/masters/expense_head").then(({ data }) => setExpenseHeads(data.map((m) => m.name))).catch(() => {});
    api
      .get("/masters/centre")
      .then(({ data }) => {
        const names = data.map((m) => m.name);
        setCentres(names);
        setHead((h) => ({ ...h, centre: h.centre || names[0] || "" }));
      })
      .catch(() => {});
    if (config.partyMaster)
      api
        .get(`/masters/${config.partyMaster}`)
        .then(({ data }) => setParties(data.map((m) => m.name)))
        .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config, editRow]);

  // One picker entry per product; shade-wise stock buckets are hidden from the picker
  const products = useMemo(() => {
    const seen = {};
    fabrics.forEach((f) => {
      const key = f.item_id || f.id;
      if (!seen[key] || !(f.shade_number || "")) seen[key] = f;
    });
    return Object.values(seen);
  }, [fabrics]);
  const byLabel = useMemo(() => Object.fromEntries(products.map((f) => [f.quality_name, f])), [products]);
  const [shadeFor, setShadeFor] = useState(null);
  const [quickItem, setQuickItem] = useState({ open: false, name: "", cell: null });

  const onItemCreated = async (item) => {
    const { data } = await api.get("/fabrics").catch(() => ({ data: null }));
    if (data) setFabrics(data);
    const cell = quickItem.cell;
    const label = item?.name || quickItem.name;
    if (cell) {
      const idx = Number(cell.dataset.lineIdx);
      if (!Number.isNaN(idx)) setLine(idx, "label", label);
      setTimeout(() => cell.focus(), 50);
    }
  };

  const [pastShades, setPastShades] = useState([]);

  useEffect(() => {
    if (centres.length && !head.centre) setHead((h) => ({ ...h, centre: centres[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centres, head.centre, open]);

  useEffect(() => {
    if (!shadeFor?.itemId) return setPastShades([]);
    api.get(`/items/${shadeFor.itemId}/shades`).then(({ data }) => setPastShades(data)).catch(() => setPastShades([]));
  }, [shadeFor]);

  useEffect(() => {
    if (!fabrics.length) return;
    setLines((ls) =>
      ls.map((l) => {
        const f = byLabel[l.label];
        if (!f) return l;
        return {
          ...l,
          fabric_id: f.id,
          product_type: f.product_type || "shade_wise",
          rate: l.rate === "" ? defaultRate(f) ?? "" : l.rate,
          gst_pct: l.gst_pct === "" ? f.gst_pct ?? "" : l.gst_pct,
        };
      })
    );
  }, [fabrics, byLabel]);

  const setLine = (idx, key, value) =>
    setLines((ls) => padRows(ls.map((l, i) => (i === idx ? { ...l, [key]: value } : l))));

  // Enter / arrows walk the grid like a DOS voucher screen
  const gridKeys = (e) => {
    if (!["Enter", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Backspace"].includes(e.key)) return;
    const cells = Array.from(gridRef.current?.querySelectorAll("input[data-grid-cell]") || []);
    const at = cells.indexOf(e.target);
    if (at === -1) return;
    // backspace on an already empty cell steps back to the previous cell
    if (e.key === "Backspace") {
      if (e.target.value) return;
      e.preventDefault();
      cells[at - 1]?.focus();
      return;
    }
    // left/right hop cells only from the edge of the text, so the caret still works inside a cell
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const el = e.target;
      const caret = el.selectionStart ?? 0;
      const atStart = caret === 0 && el.selectionEnd === 0;
      const atEnd = caret === (el.value?.length || 0) && el.selectionEnd === caret;
      if (e.key === "ArrowLeft" && !atStart) return;
      if (e.key === "ArrowRight" && !atEnd) return;
      e.preventDefault();
      cells[at + (e.key === "ArrowRight" ? 1 : -1)]?.focus();
      return;
    }
    e.preventDefault();
    if (e.key === "Enter") {
      const next = cells[at + 1];
      if (next) next.focus();
      else setLines((ls) => [...ls, { ...emptyLine }]);
      return;
    }
    const col = e.target.dataset.gridCell;
    const sameCol = cells.filter((c) => c.dataset.gridCell === col);
    const pos = sameCol.indexOf(e.target);
    const target = sameCol[e.key === "ArrowDown" ? pos + 1 : pos - 1];
    if (target) target.focus();
  };

  // product-wise subtotal: same product across many shade rows adds up
  const productTotals = useMemo(() => {
    const map = new Map();
    lines
      .filter((l) => l.fabric_id && l.label)
      .forEach((l) => {
        const q = lineQty(l);
        const prev = map.get(l.label) || { label: l.label, meter: 0, than: 0, amount: 0, rows: 0 };
        map.set(l.label, {
          ...prev,
          meter: Math.round((prev.meter + q.meter) * 100) / 100,
          than: Math.round((prev.than + q.than) * 100) / 100,
          amount: Math.round((prev.amount + lineTotal(l)) * 100) / 100,
          rows: prev.rows + 1,
        });
      });
    return Array.from(map.values());
  }, [lines]);

  const taxed = config.type !== "adjustment";
  const headGst = head.gst_pct === "" || head.gst_pct === null ? null : num(head.gst_pct);
  const gstOf = (l) => (headGst === null ? num(l.gst_pct) : headGst);
  const gross = lines.reduce((s, l) => s + num(l.rate) * lineQty(l).meter, 0);
  const lineDiscount = lines.reduce((s, l) => s + (num(l.rate) * lineQty(l).meter * num(l.discount_pct)) / 100, 0);
  const subtotal = gross - lineDiscount;
  const billDiscount = Math.min(
    num(head.discount_amount) || (subtotal * num(head.discount_pct)) / 100,
    subtotal
  );
  const otherDiscount = Math.min(
    num(head.other_discount_amount) || ((subtotal - billDiscount) * num(head.other_discount_pct)) / 100,
    subtotal - billDiscount
  );
  const lineTax = (l) => {
    const lg = num(l.rate) * lineQty(l).meter;
    const afterLine = lg - (lg * num(l.discount_pct)) / 100;
    const share = subtotal ? afterLine / subtotal : 0;
    const base = afterLine - (billDiscount + otherDiscount) * share;
    return { base, tax: base * (gstOf(l) / 100), pct: gstOf(l) };
  };
  const expAmount = (e) => {
    const pct = num(e.pct);
    return pct ? Math.round((subtotal - billDiscount - otherDiscount) * pct) / 100 : num(e.amount);
  };
  const expTaxable = expenses.reduce((s, e) => s + (e.taxable ? expAmount(e) : 0), 0);
  const expOther = expenses.reduce((s, e) => s + (e.taxable ? 0 : expAmount(e)), 0);
  const expTax = expTaxable * ((headGst || 0) / 100);
  const tax = lines.reduce((s, l) => s + lineTax(l).tax, 0) + expTax;
  const taxable = subtotal - billDiscount - otherDiscount + expTaxable;
  const netTotal = taxable + tax + expOther;
  const roundOff = head.round_off ? Math.round(netTotal) - netTotal : 0;
  const total = netTotal + roundOff;
  const totalThan = lines.reduce((s, l) => s + lineQty(l).than, 0);
  const totalQty = lines.reduce((s, l) => s + lineQty(l).meter, 0);

  // one row per GST rate, like the Busy tax summary panel
  const taxSummary = useMemo(() => {
    const map = new Map();
    lines
      .filter((l) => l.fabric_id)
      .forEach((l) => {
        const { base, tax: t, pct } = lineTax(l);
        const prev = map.get(pct) || { pct, base: 0, tax: 0 };
        map.set(pct, { pct, base: prev.base + base, tax: prev.tax + t });
      });
    return Array.from(map.values()).sort((a, b) => a.pct - b.pct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, billDiscount, subtotal, headGst]);

  // F2 saves the voucher from anywhere inside the form
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // Ctrl+F2 normally jumps to Item Master; while a voucher is open add the item in place instead
      if ((e.ctrlKey || e.metaKey) && e.key === "F2") {
        e.preventDefault();
        e.stopPropagation();
        const cell = document.activeElement?.dataset?.gridCell === "item" ? document.activeElement : null;
        setQuickItem({ open: true, name: cell?.value || "", cell });
        return;
      }
      if (e.key !== "F2" || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      formRef.current?.requestSubmit();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const items = lines
      .filter((l) => l.fabric_id)
      .map((l) => {
        const q = lineQty(l);
        return {
          fabric_id: l.fabric_id,
          shade: l.product_type === "shade_wise" ? (l.shade || "").trim() : "",
          bale_no: (l.bale_no || "").trim(),
          lr_no: (l.lr_no || "").trim(),
          than: q.than,
          meter: q.meter,
          length_text: l.product_type === "without_shade" ? (l.length_text || "").trim() : "",
          rate: l.rate === "" ? null : parseFloat(l.rate),
          discount_pct: num(l.discount_pct),
          gst_pct: l.gst_pct === "" ? null : num(l.gst_pct),
        };
      });
    if (lines.some((l) => l.label && !l.fabric_id)) {
      toast.error("Items can only be picked from the Item Master list");
      return;
    }
    if (items.length === 0) {
      toast.error("Select at least one fabric");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: config.type,
        ...head,
        discount_pct: num(head.discount_pct),
        discount_amount: num(head.discount_amount),
        other_discount_pct: num(head.other_discount_pct),
        other_discount_amount: num(head.other_discount_amount),
        round_off: head.round_off,
        gst_pct: headGst,
        expenses: expenses
          .filter((e) => (e.head || "").trim() && (num(e.amount) !== 0 || num(e.pct) !== 0))
          .map((e) => ({ head: e.head.trim(), amount: num(e.amount), pct: num(e.pct), taxable: !!e.taxable })),
        items,
      };
      const { data } = editRow
        ? await api.put(`/transactions/${editRow.id}`, payload)
        : await api.post("/transactions", payload);
      toast.success(`${config.singular} saved`);
      (data?.stock_warnings || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isPurchase = purchaseSide;
  const showGstCol = taxed && !isPurchase && headGst === null;
  const columns = isPurchase
    ? ["sn", "item", "shade", "qty", "than", "rate", "disc", "amount", "bale", "lr", "del"]
    : [
        "sn", "item", "shade", "than", "qty", "unit", "rate",
        ...(taxed ? ["disc"] : []),
        ...(showGstCol ? ["gst"] : []),
        "amount", "del",
      ];

  // Excel style fill handle: drag the little square down to copy a cell into the rows below
  const [fillDrag, setFillDrag] = useState(null);

  const startFill = (e, col, idx) => {
    e.preventDefault();
    e.stopPropagation();
    const field = FILL_FIELD[col];
    if (!field) return;
    let to = idx;
    const move = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY)?.closest("td[data-row-idx]");
      const row = el ? Number(el.dataset.rowIdx) : idx;
      to = Math.max(idx, Number.isNaN(row) ? idx : row);
      setFillDrag({ col, from: idx, to });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setFillDrag(null);
      if (to > idx) fillDown(field, idx, to);
    };
    setFillDrag({ col, from: idx, to: idx });
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const fillDown = (field, from, to) =>
    setLines((ls) => {
      const value = ls[from]?.[field] ?? "";
      const next = [...ls];
      while (next.length <= to) next.push({ ...emptyLine });
      for (let i = from + 1; i <= to; i += 1) next[i] = { ...next[i], [field]: value };
      return padRows(next);
    });

  const numCell = (idx, key, testId) => (    <input
      data-testid={testId}
      data-grid-cell={key}
      type="number"
      step="any"
      className={`font-mono-num ${cellCls} text-right`}
      value={lines[idx][key] ?? ""}
      onChange={(e) => setLine(idx, key, e.target.value)}
    />
  );

  const cell = (col, line, idx) => {
    const withoutShade = line.product_type === "without_shade";
    const pcs = line.product_type === "pcs";
    switch (col) {
      case "sn":
        return idx + 1;
      case "item":
        return (
          <div className="flex items-center">
            <input
              data-testid={`txn-line-fabric-${idx}`}
              data-grid-cell="item"
              data-line-idx={idx}
              list="txn-fabric-options"
              className={`${cellCls} min-w-0 flex-1 font-semibold`}
              value={line.label}
              onChange={(e) => {
                const label = e.target.value;
                const f = byLabel[label];
                setLines((ls) =>
                  padRows(
                    ls.map((l, i) =>
                      i === idx
                        ? {
                            ...l,
                            label,
                            fabric_id: f ? f.id : "",
                            product_type: f ? f.product_type || "shade_wise" : l.product_type,
                            rate: f && !l.rate ? defaultRate(f) ?? "" : l.rate,
                            gst_pct: f && l.gst_pct === "" ? f.gst_pct ?? "" : l.gst_pct,
                          }
                        : l
                    )
                  )
                );
              }}
            />
            {withoutShade && (
              <input
                data-testid={`txn-line-lengths-${idx}`}
                data-grid-cell="lengths"
                title="Length pieces e.g. 10.8*1 12.5*1"
                placeholder="10.8*1 12.5*1"
                className={`${cellCls} !w-[150px] shrink-0 border-l border-[#D9D9B5] text-[12px]`}
                value={line.length_text}
                onChange={(e) => setLine(idx, "length_text", e.target.value)}
              />
            )}
            {!line.fabric_id && (
              <button
                type="button"
                title="Add this as a new item (Ctrl+F2)"
                data-testid={`txn-line-new-item-${idx}`}
                onClick={(e) =>
                  setQuickItem({
                    open: true,
                    name: line.label || "",
                    cell: e.currentTarget.parentElement.querySelector('input[data-grid-cell="item"]'),
                  })
                }
                className="shrink-0 px-1 text-[var(--mod-primary)] hover:opacity-70"
              >
                <Plus size={13} />
              </button>
            )}
            {line.product_type === "shade_wise" && !!line.fabric_id && (
              <button
                type="button"
                title="Shade details"
                data-testid={`txn-line-shades-${idx}`}
                onClick={() =>
                  setShadeFor({ idx, product: line.label.trim(), itemId: byLabel[line.label.trim()]?.item_id })
                }
                className="shrink-0 px-1 text-[#7A0000] hover:text-black"
              >
                <Layers size={13} />
              </button>
            )}
          </div>
        );
      case "shade":
        return (
          <input
            data-testid={`txn-line-shade-${idx}`}
            data-grid-cell="shade"
            disabled={line.product_type !== "shade_wise"}
            className={`${cellCls} text-center disabled:bg-[#F2F2E4]`}
            value={line.shade}
            onChange={(e) => setLine(idx, "shade", e.target.value)}
          />
        );
      case "than":
        if (withoutShade || pcs)
          return (
            <span className="font-mono-num block px-1.5 text-right text-[12px] text-zinc-500">
              {pcs ? "—" : lineQty(line).than || ""}
            </span>
          );
        return numCell(idx, "than", `txn-line-than-${idx}`);
      case "qty":
        if (withoutShade)
          return (
            <span
              data-testid={`txn-line-lengths-total-${idx}`}
              className="font-mono-num block px-1.5 text-right text-[12px] text-zinc-600"
            >
              {lineQty(line).meter || ""}
            </span>
          );
        return numCell(idx, "meter", `txn-line-meter-${idx}`);
      case "unit":
        return <span className="text-[12px] text-zinc-600">{line.fabric_id ? (pcs ? "Pcs" : "Mtr") : ""}</span>;
      case "rate":
        return numCell(idx, "rate", `txn-line-rate-${idx}`);
      case "disc":
        return numCell(idx, "discount_pct", `txn-line-discount-${idx}`);
      case "gst":
        return numCell(idx, "gst_pct", `txn-line-gst-${idx}`);
      case "bale":
        return (
          <input
            data-testid={`txn-line-bale-${idx}`}
            data-grid-cell="bale"
            className={`${cellCls} text-center`}
            value={line.bale_no}
            onChange={(e) => setLine(idx, "bale_no", e.target.value)}
          />
        );
      case "lr":
        return (
          <input
            data-testid={`txn-line-lr-${idx}`}
            data-grid-cell="lr"
            className={`${cellCls} text-center`}
            value={line.lr_no}
            onChange={(e) => setLine(idx, "lr_no", e.target.value)}
          />
        );
      case "amount":
        return (
          <span
            data-testid={`txn-line-total-${idx}`}
            className="font-mono-num block text-right text-[13px] font-semibold text-zinc-900"
          >
            {line.fabric_id ? rs(lineTotal(line)) : ""}
          </span>
        );
      default:
        return (
          <button
            type="button"
            data-testid={`txn-line-remove-${idx}`}
            onClick={() => setLines((ls) => padRows(ls.filter((_, i) => i !== idx)))}
            className="px-0.5 text-zinc-400 hover:text-red-700"
          >
            <Trash2 size={12} />
          </button>
        );
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          data-testid="txn-form-dialog"
          className="mod-theme left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-auto rounded-none border-0 p-0 sm:rounded-none lg:w-[calc(100vw-var(--sb-w))]"
          onInteractOutside={(e) => e.preventDefault()}
          style={{ ...themeVars(formTheme), background: formTheme.tint }}
        >
          <DialogTitle className="sr-only">{editRow ? `Edit ${config.singular}` : `Add ${config.singular} Voucher`}</DialogTitle>
          <DialogDescription className="sr-only">{config.hint}</DialogDescription>

          <form ref={formRef} onSubmit={submit} className="min-w-[1100px] px-3 pb-3">
            <div className="flex justify-center py-1.5">
              <span data-testid="txn-voucher-title" className="bg-[#9C3B3B] px-6 py-1 text-[13px] font-bold text-white">
                {editRow ? `Edit ${config.singular} Voucher` : `Add ${config.singular} Voucher`}
              </span>
            </div>

            {/* voucher header strip */}
            <div className="grid grid-cols-[repeat(6,minmax(0,1fr))] items-center gap-x-3 gap-y-1.5 border border-[#B9B98A] bg-[#FFFFF0] px-2 py-2">
              <div className="flex items-center gap-2">
                <Lbl>Series</Lbl>
                <span className="text-[13px] font-bold text-[#1F3D7A]">Main</span>
              </div>
              <div className="flex items-center gap-2">
                <Lbl>Date</Lbl>
                <DateInput
                  data-testid="txn-date-input"
                  className={`w-[125px] ${fieldCls}`}
                  inputClassName="h-full w-full border-0 bg-transparent p-0 text-[13px] font-semibold text-[var(--mod-dark)] outline-none"
                  value={head.date}
                  onChange={(v) => setHead((h) => ({ ...h, date: v }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <Lbl>{config.type === "adjustment" ? "Reason" : "Vch No."}</Lbl>
                <input
                  data-testid="txn-ref-input"
                  required
                  className={`${fieldCls} w-[110px]`}
                  value={head.ref_no}
                  onChange={(e) => setHead((h) => ({ ...h, ref_no: e.target.value }))}
                />
              </div>
              {isPurchase && (
                <div className="flex items-center gap-2">
                  <Lbl>Purc. Bill No.</Lbl>
                  <input
                    data-testid="txn-bill-no-input"
                    className={`${fieldCls} w-[120px]`}
                    placeholder="Supplier bill no."
                    value={head.bill_no}
                    onChange={(e) => setHead((h) => ({ ...h, bill_no: e.target.value }))}
                  />
                </div>
              )}
              <div className="col-span-2 flex items-center gap-2">
                <Lbl>{config.type === "transfer" ? "From Centre" : "Mat. Centre"}</Lbl>
                <div className="w-[190px]">
                  <MasterSelect
                    testId="txn-centre-input"
                    className="mt-0 h-[24px] rounded-none border-[#B9B98A] bg-white px-1.5 text-[13px] font-semibold text-[#1F3D7A]"
                    options={centres}
                    value={head.centre}
                    onChange={(v) => setHead((h) => ({ ...h, centre: v }))}
                    placeholder="Select centre"
                    emptyHint="Add centres in Admin → Material Centre Master"
                  />
                </div>
              </div>
              {taxed && (
                <div className="flex items-center gap-2">
                  <Lbl>Type</Lbl>
                  <div className="flex">
                    {[
                      { key: false, label: "Local", tid: "txn-gst-local-button" },
                      { key: true, label: "Interstate", tid: "txn-gst-interstate-button" },
                    ].map((opt) => (
                      <button
                        key={String(opt.key)}
                        type="button"
                        data-testid={opt.tid}
                        onClick={() => setHead((h) => ({ ...h, interstate: opt.key }))}
                        className={`border border-[#B9B98A] px-2 py-[3px] text-[12px] font-semibold ${
                          head.interstate === opt.key ? "bg-[#9C3B3B] text-white" : "bg-[#E8E8CE] text-zinc-700"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {taxed && (
                <div className="flex items-center gap-2">
                  <Lbl>GST Rate</Lbl>
                  <select
                    data-testid="txn-gst-rate-select"
                    className={`${fieldCls} w-[110px]`}
                    value={head.gst_pct === "" ? "" : String(head.gst_pct)}
                    onChange={(e) => setHead((h) => ({ ...h, gst_pct: e.target.value }))}
                  >
                    <option value="">Item wise</option>
                    {[0, 0.1, 3, 5, 12, 18, 28].map((r) => (
                      <option key={r} value={r}>
                        {r === 0 ? "Exempt (0%)" : `GST ${r}%`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {config.partyLabel && (
                <div className="col-span-3 flex items-center gap-2">
                  <Lbl>{config.partyLabel}</Lbl>
                  {config.partyMaster ? (
                    <div className="w-[260px]">
                      <MasterSelect
                        testId="txn-party-input"
                        className="mt-0 h-[24px] rounded-none border-[#B9B98A] bg-white px-1.5 text-[13px] font-semibold text-[#1F3D7A]"
                        options={parties}
                        value={head.party}
                        onChange={(v) => setHead((h) => ({ ...h, party: v }))}
                        placeholder={`Select ${config.partyLabel.toLowerCase()}`}
                        emptyHint={`Add ${config.partyLabel.toLowerCase()}s in Admin → ${config.partyLabel} Master`}
                      />
                    </div>
                  ) : (
                    <input
                      data-testid="txn-party-input"
                      className={`${fieldCls} w-[260px]`}
                      value={head.party}
                      onChange={(e) => setHead((h) => ({ ...h, party: e.target.value }))}
                    />
                  )}
                </div>
              )}
              {config.type === "transfer" && (
                <div className="col-span-2 flex items-center gap-2">
                  <Lbl>To Centre</Lbl>
                  <div className="w-[190px]">
                    <MasterSelect
                      testId="txn-to-centre-input"
                      className="mt-0 h-[24px] rounded-none border-[#B9B98A] bg-white px-1.5 text-[13px] font-semibold text-[#1F3D7A]"
                      options={centres}
                      value={head.to_centre}
                      onChange={(v) => setHead((h) => ({ ...h, to_centre: v }))}
                      placeholder="Select target centre"
                      emptyHint="Add centres in Admin → Material Centre Master"
                    />
                  </div>
                </div>
              )}
              <div className="col-span-3 flex items-center gap-2">
                <Lbl>Narration</Lbl>
                <input
                  data-testid="txn-notes-input"
                  className={`${fieldCls} flex-1`}
                  value={head.notes}
                  onChange={(e) => setHead((h) => ({ ...h, notes: e.target.value }))}
                />
              </div>
            </div>

            {/* item grid */}
            <div className="mt-2" ref={gridRef} onKeyDown={gridKeys}>
              <table className="w-full border-collapse table-fixed">
                <colgroup>
                  {columns.map((c) => (
                    <col key={c} className={COL_WIDTH[c] || ""} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c} className={`${headCell} ${c === "item" ? "text-left" : ""}`}>
                        {COL_LABEL[c]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={idx} data-testid={`txn-line-${idx}`} className="odd:bg-white even:bg-[var(--mod-stripe)] hover:bg-[var(--mod-hover)]">
                      {columns.map((c) => (
                        <td
                          key={c}
                          data-row-idx={idx}
                          data-col={c}
                          className={`relative ${bodyCell} ${c === "sn" ? "bg-[var(--mod-head)] text-center text-[12px] font-semibold text-zinc-600" : ""} ${
                            c === "unit" || c === "amount" ? "px-1.5" : ""
                          } ${c === "del" ? "text-center" : ""} ${
                            fillDrag && fillDrag.col === c && idx > fillDrag.from && idx <= fillDrag.to
                              ? "ring-1 ring-inset ring-[var(--mod-primary)]"
                              : ""
                          } group/cell`}
                        >
                          {cell(c, line, idx)}
                          {FILL_FIELD[c] && (
                            <span
                              data-testid={`txn-fill-handle-${idx}-${c}`}
                              title="Drag down to copy this value into the rows below"
                              onMouseDown={(e) => startFill(e, c, idx)}
                              className="absolute -bottom-[2px] -right-[2px] h-[7px] w-[7px] cursor-crosshair bg-[var(--mod-primary)] opacity-0 transition-opacity group-hover/cell:opacity-100"
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <datalist id="txn-fabric-options">
                {products.map((f) => (
                  <option key={f.id} value={f.quality_name} />
                ))}
              </datalist>
            </div>

            {/* grid footer totals */}
            <div className="flex items-center justify-between border-x border-b border-[#B9B98A] bg-[#E8E8CE] px-2 py-1">
              <button
                type="button"
                data-testid="txn-add-line-button"
                onClick={() => setLines((ls) => [...ls, { ...emptyLine }])}
                className="border border-[#B9B98A] bg-[#F4F4E2] px-2 py-[2px] text-[12px] font-semibold text-zinc-700 hover:bg-white"
              >
                Add Row (Enter)
              </button>
              <div className="font-mono-num flex items-center gap-6 text-[13px] font-bold">
                <span>{totalQty.toFixed(2)}</span>
                <span className="text-[12px] font-normal text-zinc-600">(Alt. Qty. = {totalThan.toFixed(2)} than)</span>
                <span data-testid={taxed ? "txn-gross" : "txn-total"}>{rs(taxed ? gross : total)}</span>
              </div>
            </div>

            {/* lower panels */}
            <div className="mt-2 grid grid-cols-[1.1fr_1.2fr_1fr] gap-2">
              {taxed ? (
                <Box title="Tax Summary">
                  <table className="w-full border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className={`${headCell} text-right`}>Tax Rate</th>
                        <th className={`${headCell} text-right`}>Taxable Amt.</th>
                        <th className={`${headCell} text-right`}>{head.interstate ? "IGST" : "CGST"}</th>
                        {!head.interstate && <th className={`${headCell} text-right`}>SGST</th>}
                      </tr>
                    </thead>
                    <tbody data-testid="txn-tax-summary">
                      {taxSummary.length === 0 ? (
                        <tr>
                          <td className={`${bodyCell} px-1.5 py-1 text-center text-zinc-400`} colSpan={4}>
                            No taxable rows yet
                          </td>
                        </tr>
                      ) : (
                        taxSummary.map((t) => (
                          <tr key={t.pct} data-testid={`txn-tax-row-${t.pct}`}>
                            <td className={`${bodyCell} font-mono-num px-1.5 text-right`}>{t.pct}%</td>
                            <td className={`${bodyCell} font-mono-num px-1.5 text-right`}>{rs(t.base)}</td>
                            <td className={`${bodyCell} font-mono-num px-1.5 text-right`}>
                              {rs(head.interstate ? t.tax : t.tax / 2)}
                            </td>
                            {!head.interstate && (
                              <td className={`${bodyCell} font-mono-num px-1.5 text-right`}>{rs(t.tax / 2)}</td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Box title="Voucher Info">
                  <p className="pt-1 text-[12px] text-zinc-600">{config.hint}</p>
                </Box>
              )}

              <Box title="Bill Sundry">
                <div className="space-y-1.5 pt-1">
                  {isPurchase && (
                    <div className="mb-2 border-b border-[#D9D9B5] pb-2" data-testid="txn-expenses-panel">
                      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#7A0000]">
                        Bill Expenses <span className="font-normal normal-case text-zinc-500">(type % of taxable value or a flat amount)</span>
                      </p>
                      {expenses.map((ex, i) => (
                        <div key={i} className="mb-1 flex items-center gap-1" data-testid={`txn-expense-row-${i}`}>
                          <input
                            data-testid={`txn-expense-head-${i}`}
                            list="txn-expense-heads"
                            placeholder="Packing & Forwarding"
                            className={`${fieldCls} min-w-0 flex-1`}
                            value={ex.head}
                            onChange={(e) =>
                              setExpenses((xs) => xs.map((x, j) => (j === i ? { ...x, head: e.target.value } : x)))
                            }
                          />
                          <input
                            data-testid={`txn-expense-pct-${i}`}
                            type="number"
                            step="any"
                            placeholder="%"
                            title="Percent of taxable value, e.g. 0.6"
                            className={`font-mono-num ${fieldCls} w-[58px] text-right`}
                            value={ex.pct ?? ""}
                            onChange={(e) =>
                              setExpenses((xs) =>
                                xs.map((x, j) => (j === i ? { ...x, pct: e.target.value, amount: "" } : x))
                              )
                            }
                          />
                          {num(ex.pct) ? (
                            <span
                              data-testid={`txn-expense-amount-${i}`}
                              className="font-mono-num w-[80px] shrink-0 border border-[#B9B98A] bg-[#F2F2E4] px-1.5 text-right text-[13px] font-semibold text-[#1F3D7A]"
                            >
                              {rs(expAmount(ex))}
                            </span>
                          ) : (
                            <input
                              data-testid={`txn-expense-amount-${i}`}
                              type="number"
                              step="any"
                              placeholder="0"
                              className={`font-mono-num ${fieldCls} w-[80px] text-right`}
                              value={ex.amount}
                              onChange={(e) =>
                                setExpenses((xs) => xs.map((x, j) => (j === i ? { ...x, amount: e.target.value, pct: "" } : x)))
                              }
                            />
                          )}
                          <label className="flex items-center gap-1 text-[11px] font-semibold text-[#5B4A00]">
                            <input
                              data-testid={`txn-expense-taxable-${i}`}
                              type="checkbox"
                              className="h-3 w-3 accent-[#7A0000]"
                              checked={!!ex.taxable}
                              onChange={(e) =>
                                setExpenses((xs) => xs.map((x, j) => (j === i ? { ...x, taxable: e.target.checked } : x)))
                              }
                            />
                            GST
                          </label>
                          <button
                            type="button"
                            data-testid={`txn-expense-remove-${i}`}
                            onClick={() => setExpenses((xs) => xs.filter((_, j) => j !== i))}
                            className="px-0.5 text-zinc-400 hover:text-red-700"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                      <datalist id="txn-expense-heads">
                        {expenseHeads.map((h) => (
                          <option key={h} value={h} />
                        ))}
                      </datalist>
                      <button
                        type="button"
                        data-testid="txn-add-expense-button"
                        onClick={() => setExpenses((xs) => [...xs, { head: "", amount: "", pct: "", taxable: false }])}
                        className="border border-[#B9B98A] bg-[#F4F4E2] px-2 py-[2px] text-[11px] font-semibold text-zinc-700 hover:bg-white"
                      >
                        + Add Expense
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <Lbl>Bill Discount %</Lbl>
                    <input
                      data-testid="txn-discount-pct-input"
                      type="number"
                      step="any"
                      className={`font-mono-num ${fieldCls} w-[100px] text-right`}
                      value={head.discount_pct}
                      onChange={(e) => setHead((h) => ({ ...h, discount_pct: e.target.value, discount_amount: "" }))}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Lbl>Or Discount Rs.</Lbl>
                    <input
                      data-testid="txn-discount-amount-input"
                      type="number"
                      step="any"
                      className={`font-mono-num ${fieldCls} w-[100px] text-right`}
                      value={head.discount_amount}
                      onChange={(e) => setHead((h) => ({ ...h, discount_amount: e.target.value, discount_pct: "" }))}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Lbl>Other Disc %</Lbl>
                    <input
                      data-testid="txn-other-discount-pct-input"
                      type="number"
                      step="any"
                      title="Second discount, applied after the bill discount"
                      className={`font-mono-num ${fieldCls} w-[100px] text-right`}
                      value={head.other_discount_pct}
                      onChange={(e) => setHead((h) => ({ ...h, other_discount_pct: e.target.value, other_discount_amount: "" }))}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Lbl>Or Other Disc Rs.</Lbl>
                    <input
                      data-testid="txn-other-discount-amount-input"
                      type="number"
                      step="any"
                      className={`font-mono-num ${fieldCls} w-[100px] text-right`}
                      value={head.other_discount_amount}
                      onChange={(e) => setHead((h) => ({ ...h, other_discount_amount: e.target.value, other_discount_pct: "" }))}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-[12px] font-semibold text-[#5B4A00]">
                    <input
                      data-testid="txn-round-off-checkbox"
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-[#7A0000]"
                      checked={head.round_off}
                      onChange={(e) => setHead((h) => ({ ...h, round_off: e.target.checked }))}
                    />
                    Round off to nearest rupee
                  </label>
                  <div className="font-mono-num mt-1 space-y-0.5 border-t border-[#D9D9B5] pt-1.5 text-[12px]" data-testid="txn-totals-panel">
                    {[
                      ["Gross", gross],
                      ["Line Discount", -lineDiscount],
                      ["Bill Discount", -billDiscount],
                      ...(otherDiscount ? [["Other Discount", -otherDiscount]] : []),
                      ["Taxable Value", taxable],
                      ...(taxed
                        ? head.interstate
                          ? [["IGST", tax]]
                          : [["CGST", tax / 2], ["SGST", tax / 2]]
                        : []),
                      ...(expOther ? [["Expenses", expOther]] : []),
                      ...(head.round_off ? [["Round Off", roundOff]] : []),
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-4">
                        <span className="text-zinc-600">{label}</span>
                        <span>{rs(value)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between gap-4 border-t border-[#B9B98A] pt-1 text-[14px] font-bold">
                      <span>Grand Total</span>
                      <span data-testid="txn-total">{rs(total)}</span>
                    </div>
                  </div>
                </div>
              </Box>

              <Box title="Item Info">
                <div className="max-h-[210px] overflow-auto pt-1" data-testid="txn-item-totals">
                  {productTotals.length === 0 ? (
                    <p className="text-[12px] text-zinc-400">Pick items to see item-wise totals</p>
                  ) : (
                    productTotals.map((p) => (
                      <div
                        key={p.label}
                        data-testid={`txn-item-total-${p.label}`}
                        className="border-b border-[#EAEAD2] py-1 text-[12px] last:border-b-0"
                      >
                        <p className="font-semibold uppercase text-zinc-800">
                          {p.label} <span className="font-normal text-zinc-400">· {p.rows} row(s)</span>
                        </p>
                        <p className="font-mono-num text-zinc-600">
                          {p.meter} mtr · {p.than} than · <span className="font-bold text-zinc-900">{rs(p.amount)}</span>
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </Box>
            </div>

            {/* action bar */}
            <div className="mt-2 flex items-center gap-2 border border-[#B9B98A] bg-[#E8E8CE] px-2 py-1.5">
              <button
                data-testid="txn-save-button"
                type="submit"
                disabled={saving}
                className="border border-[#8B7500] bg-[#F4F4E2] px-6 py-[3px] text-[13px] font-bold text-zinc-900 hover:bg-white disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save (F2)"}
              </button>
              <button
                data-testid="txn-quit-button"
                type="button"
                onClick={() => onOpenChange(false)}
                className="border border-[#8B7500] bg-[#F4F4E2] px-6 py-[3px] text-[13px] font-bold text-zinc-900 hover:bg-white"
              >
                Quit
              </button>
              <span className="ml-auto text-[11px] text-zinc-600">
                Enter / ↓ ↑ ← → move between cells · drag the small square at a cell corner to copy it down · Esc quits
              </span>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <QuickItemDialog
        open={quickItem.open}
        onOpenChange={(o) => setQuickItem((q) => ({ ...q, open: o }))}
        initialName={quickItem.name}
        onCreated={onItemCreated}
      />

      <ShadeDialog
        open={!!shadeFor}
        onOpenChange={(o) => !o && setShadeFor(null)}
        product={shadeFor?.product || ""}
        pastShades={pastShades}
        initialRows={
          shadeFor
            ? lines
                .filter((l) => l.label.trim() === shadeFor.product && (l.shade || "").trim())
                .map((l) => ({ shade: l.shade, meter: l.meter, than: l.than }))
            : []
        }
        onConfirm={(rows) => {
          const at = shadeFor.idx;
          setLines((ls) => {
            const base = ls[at];
            const kept = ls.filter((l, i) => i !== at && !(l.label.trim() === shadeFor.product && (l.shade || "").trim()));
            const added = rows.map((r) => ({
              ...base,
              label: shadeFor.product,
              product_type: "shade_wise",
              length_text: "",
              shade: r.shade,
              meter: r.meter,
              than: r.than,
            }));
            const headRows = kept.slice(0, at);
            return padRows([...headRows, ...added, ...kept.slice(at)]);
          });
          setShadeFor(null);
        }}
      />
    </>
  );
};
